import { Car, PhoneCall, ShieldCheck, ShoppingBag } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import { Button, Card } from "react-daisyui";

// Import app store icons
import appGalleryIcon from "@/assets/apps/appgallery.png";
import appStoreIcon from "@/assets/apps/appstore.png";
import playStoreIcon from "@/assets/apps/playstore.jpg";
import webIcon from "@/assets/apps/web.jfif";
import {
  fetchAppDownloadSection,
  fetchTransportAppDownloadSection,
} from "@/services/landing/hareengEatsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

export default function Hero() {
  const [showTransportQR, setShowTransportQR] = useState(false);
  const [showFoodQR, setShowFoodQR] = useState(false);
  const [transportActiveTab, setTransportActiveTab] = useState<string>("android");
  const [foodActiveTab, setFoodActiveTab] = useState<string>("android");

  // Transport section state
  const [transportUrl, setTransportUrl] = useState<string>("");
  const [transportIosUrl, setTransportIosUrl] = useState<string>("");
  const [transportAndroidUrl, setTransportAndroidUrl] = useState<string>("");
  const [transportWebUrl, setTransportWebUrl] = useState<string>("");
  const [transportAppGalleryUrl, setTransportAppGalleryUrl] = useState<string>("");

  // Food section state
  const [foodUrl, setFoodUrl] = useState<string>("");
  const [foodIosUrl, setFoodIosUrl] = useState<string>("");
  const [foodAndroidUrl, setFoodAndroidUrl] = useState<string>("");
  const [foodWebUrl, setFoodWebUrl] = useState<string>("");
  const [foodAppGalleryUrl, setFoodAppGalleryUrl] = useState<string>("");

  // Hero content state
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");
  const [loading, setLoading] = useState(true);

  // Hero background state
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    fetchHeroBackgrounds("hero").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  useEffect(() => {
    Promise.all([
      fetchTransportAppDownloadSection(),
      fetchAppDownloadSection(),
    ]).then(([transportSection, foodSection]) => {
      if (transportSection) {
        setTransportUrl(
          transportSection.androidUrl || transportSection.iosUrl || transportSection.webUrl || transportSection.appGalleryUrl ||"",
        );
        setTransportIosUrl(transportSection.iosUrl || "");
        setTransportAndroidUrl(transportSection.androidUrl || "");
        setTransportWebUrl(transportSection.webUrl || "");
        setTransportAppGalleryUrl(transportSection.appGalleryUrl || "");
      }
      if (foodSection) {
        setFoodUrl(foodSection.androidUrl || foodSection.iosUrl || foodSection.webUrl || "");
        setFoodIosUrl(foodSection.iosUrl || "");
        setFoodAndroidUrl(foodSection.androidUrl || "");
        setFoodWebUrl(foodSection.webUrl || "");
        setFoodAppGalleryUrl(foodSection.appGalleryUrl || "");
        setSectionTitle(foodSection.title || "Get There. Get Food.");
        setSectionDescription(
          foodSection.description ||
            "Book rides, order food, and enjoy reliable delivery—all from one seamless platform.",
        );
      }
      setLoading(false);
    });
  }, []);

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background Layer */}
      <div className="absolute inset-0 opacity-40">
        {heroLoading ? (
          <div className="relative h-full w-full overflow-hidden bg-base-200">
            <div
              className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
              style={{ backgroundSize: "200% 100%" }}
            />
          </div>
        ) : heroBackground?.imageUrl ? (
          <img
            alt={heroBackground?.imageAlt || "Hero background"}
            className="h-full w-full object-cover"
            src={heroBackground.imageUrl}
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-base-200 to-base-100" />
        )}
      </div>

      {/* Content Layer */}
      <div className="relative flex flex-col items-center justify-center px-4 pb-20 pt-32 sm:pt-40 md:pb-32 md:pt-48">
        {/* Hero Content */}
        <div className="max-w-2xl text-center">
          {loading ? (
            <>
              <ModernShimmer
                className="mx-auto mb-4 sm:h-16 md:h-20"
                height="4rem"
                width="75%"
              />
              <ModernShimmer
                className="mx-auto mb-8 sm:h-8"
                height="2rem"
                width="66%"
              />
            </>
          ) : (
            <>
              <h1 className="mb-4 text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl">
                {sectionTitle} <br />
                <span className="text-primary">All in One App</span>
              </h1>
              <p className="mb-8 text-base text-base-content/70 sm:text-lg md:text-xl">
                {sectionDescription}
              </p>
            </>
          )}
          <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Button
              className="font-semibold"
              color="primary"
              disabled={!transportAndroidUrl}
              onClick={() => {
                setTransportActiveTab("android");
                setShowTransportQR(true);
              }}
              size="lg"
            >
              Download Our Apps
            </Button>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="w-full max-w-6xl px-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              desc="Get picked up in minutes with nearby drivers."
              icon={<Car className="h-8 w-8 text-primary" />}
              title="Quick Rides"
            />
            <FeatureCard
              desc="Order from your favorite restaurants, Send packages fast."
              icon={<ShoppingBag className="h-8 w-8 text-secondary" />}
              title="Delivery"
            />
            <FeatureCard
              desc="Every trip and delivery is secure and insured."
              icon={<ShieldCheck className="h-8 w-8 text-green-500" />}
              title="Safe & Reliable"
            />
            <FeatureCard
              desc="Always here to assist you with any issues."
              icon={<PhoneCall className="h-8 w-8 text-blue-500" />}
              title="24/7 Support"
            />
          </div>
        </div>
      </div>

      {/* Transport QR Code Modal */}
      {showTransportQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-4">
          <div className="relative flex w-full max-w-md flex-col rounded-3xl bg-white shadow-2xl dark:bg-base-800 sm:w-96">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-base-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-bold text-base-content">Download Our Apps</h3>
                <p className="text-sm text-base-content/60">Choose your platform</p>
              </div>
              <button
                aria-label="Close"
                className="flex h-10 w-10 items-center justify-center rounded-full text-xl font-bold text-base-content/50 transition-colors hover:bg-base-200 hover:text-primary"
                onClick={() => setShowTransportQR(false)}
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-base-200 px-6 py-3 overflow-x-auto">
              {transportAndroidUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    transportActiveTab === "android"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setTransportActiveTab("android")}
                >
                  <img alt="Play Store" className="h-5 w-5" src={playStoreIcon} />
                  <span>Android</span>
                </button>
              )}
              {transportIosUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    transportActiveTab === "ios"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setTransportActiveTab("ios")}
                >
                  <img alt="App Store" className="h-5 w-5" src={appStoreIcon} />
                  <span>iOS</span>
                </button>
              )}
              {transportWebUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    transportActiveTab === "web"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setTransportActiveTab("web")}
                >
                  <img alt="Web" className="h-5 w-5" src={webIcon} />
                  <span>Web</span>
                </button>
              )}
              {transportAppGalleryUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    transportActiveTab === "appgallery"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setTransportActiveTab("appgallery")}
                >
                  <img alt="App Gallery" className="h-5 w-5" src={appGalleryIcon} />
                  <span>AppGallery</span>
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex flex-col items-center px-6 py-8">
              {/* QR Code Container */}
              <div className="mb-6 flex items-center justify-center bg-white p-4 rounded-lg">
                <QRCodeCanvas
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={true}
                  level="M"
                  size={240}
                  value={
                    transportActiveTab === "android"
                      ? transportAndroidUrl
                      : transportActiveTab === "ios"
                        ? transportIosUrl
                        : transportActiveTab === "web"
                          ? transportWebUrl
                          : transportAppGalleryUrl
                  }
                />
              </div>

              {/* Description */}
              <p className="mb-4 text-center text-sm text-base-content/70">
                Scan with your phone camera
              </p>

              {/* Direct Link Button */}
              <a
                className="btn btn-primary w-full"
                href={
                  transportActiveTab === "android"
                    ? transportAndroidUrl
                    : transportActiveTab === "ios"
                      ? transportIosUrl
                      : transportActiveTab === "web"
                        ? transportWebUrl
                        : transportAppGalleryUrl
                }
                rel="noopener noreferrer"
                target="_blank"
              >
                Open {transportActiveTab === "android" ? "Android" : transportActiveTab === "ios" ? "iOS" : transportActiveTab === "web" ? "Web" : "App Gallery"} Store
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Food QR Code Modal */}
      {showFoodQR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4">
          <div className="relative flex w-full max-w-md flex-col rounded-3xl bg-white shadow-2xl dark:bg-base-800 sm:w-96">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-base-200 px-6 py-4">
              <div>
                <h3 className="text-xl font-bold text-base-content">Download Our Apps</h3>
                <p className="text-sm text-base-content/100">Choose your platform</p>
              </div>
              <button
                aria-label="Close"
                className="flex h-10 w-10 items-center justify-center rounded-full text-xl font-bold text-base-content/50 transition-colors hover:bg-base-200 hover:text-primary"
                onClick={() => setShowFoodQR(false)}
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-base-200 px-6 py-3 overflow-x-auto">
              {foodAndroidUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    foodActiveTab === "android"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setFoodActiveTab("android")}
                >
                  <img alt="Play Store" className="h-5 w-5" src={playStoreIcon} />
                  <span>Android</span>
                </button>
              )}
              {foodIosUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    foodActiveTab === "ios"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setFoodActiveTab("ios")}
                >
                  <img alt="App Store" className="h-5 w-5" src={appStoreIcon} />
                  <span>iOS</span>
                </button>
              )}
              {foodWebUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    foodActiveTab === "web"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setFoodActiveTab("web")}
                >
                  <img alt="Web" className="h-5 w-5" src={webIcon} />
                  <span>Web</span>
                </button>
              )}
              {foodAppGalleryUrl && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                    foodActiveTab === "appgallery"
                      ? "border-b-2 border-primary bg-primary/10 text-primary"
                      : "text-base-content/60 hover:text-base-content"
                  }`}
                  onClick={() => setFoodActiveTab("appgallery")}
                >
                  <img alt="App Gallery" className="h-5 w-5" src={appGalleryIcon} />
                  <span>AppGallery</span>
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex flex-col items-center px-6 py-8">
              {/* QR Code Container */}
              <div className="mb-6 flex items-center justify-center bg-white p-4 rounded-lg">
                <QRCodeCanvas
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={true}
                  level="M"
                  size={240}
                  value={
                    foodActiveTab === "android"
                      ? foodAndroidUrl
                      : foodActiveTab === "ios"
                        ? foodIosUrl
                        : foodActiveTab === "web"
                          ? foodWebUrl
                          : foodAppGalleryUrl
                  }
                />
              </div>

              {/* Description */}
              <p className="mb-4 text-center text-sm text-base-content/70">
                Scan with your phone camera
              </p>

              {/* Direct Link Button */}
              <a
                className="btn btn-primary w-full"
                href={
                  foodActiveTab === "android"
                    ? foodAndroidUrl
                    : foodActiveTab === "ios"
                      ? foodIosUrl
                      : foodActiveTab === "web"
                        ? foodWebUrl
                        : foodAppGalleryUrl
                }
                rel="noopener noreferrer"
                target="_blank"
              >
                Open {foodActiveTab === "android" ? "Android" : foodActiveTab === "ios" ? "iOS" : foodActiveTab === "web" ? "Web" : "App Gallery"} Store
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function FeatureCard({
  desc,
  icon,
  title,
}: {
  desc: string;
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <Card className="flex flex-col items-center rounded-xl border border-base-200 bg-base-100 p-6 text-center shadow-md">
      <div className="mb-3">{icon}</div>
      <Card.Title className="mb-1 text-lg font-bold" tag="h3">
        {title}
      </Card.Title>
      <p className="text-sm text-base-content/70">{desc}</p>
    </Card>
  );
}

// Add shimmer animation to global styles if not present
if (
  typeof document !== "undefined" &&
  !document.getElementById("modern-shimmer-style")
) {
  const style = document.createElement("style");
  style.id = "modern-shimmer-style";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    .animate-shimmer {
      animation: shimmer 1.5s linear infinite;
    }
  `;
  document.head.appendChild(style);
}

// Modern shimmer component
function ModernShimmer({
  className = "",
  height,
  width,
}: {
  className?: string;
  height: string;
  width: string;
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl shadow-sm ${className}`}
      style={{ height, width }}
    >
      <div
        className="animate-shimmer absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-base-200 dark:via-base-100 dark:to-base-200"
        style={{ backgroundSize: "200% 100%" }}
      />
    </div>
  );
}
